import { Link } from "react-router-dom";
import { Linkedin, Users } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";
import { LOGO_URL } from "@/lib/api";

export default function Footer({ content }) {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-[#171138] text-white/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-10">
          <div className="max-w-sm">
            <div className="flex items-center gap-3">
              <img src={content?.logo_url || LOGO_URL} alt="ScholarVerse LAB logo" className="h-10 w-10 rounded-full bg-white object-cover" />
              <span className="font-serif text-xl font-bold text-white">
                ScholarVerse <span className="text-cherry">LAB</span>
              </span>
            </div>
            <p className="mt-4 font-serif italic text-lg text-sky/80">Learn the thinking behind research.</p>
            <div className="mt-5 flex items-center gap-3">
              {content?.linkedin_url && (
                <a
                  href={content.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-linkedin-link"
                  aria-label="LinkedIn"
                  className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-cherry hover:bg-cherry/20 transition-all duration-300"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              )}
              {content?.whatsapp_group_url && (
                <a
                  href={content.whatsapp_group_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-group-link"
                  aria-label="WhatsApp Community"
                  className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-cherry hover:bg-cherry/20 transition-all duration-300"
                >
                  <Users className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          <div className="flex gap-16">
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-4">Explore</p>
              <ul className="space-y-2.5 text-sm font-sans">
                {[["about", "About"], ["founder", "Founders"], ["courses", "Courses"], ["live", "Live Workshop"], ["community", "Community"]].map(([id, label]) => (
                  <li key={id}>
                    <button onClick={() => scrollToId(id)} data-testid={`footer-link-${id}`} className="hover:text-white transition-colors">
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-4">Contact</p>
              <ul className="space-y-2.5 text-sm font-sans">
                <li><a href={`mailto:${content?.email}`} className="hover:text-white transition-colors">{content?.email}</a></li>
                <li><a href={`https://wa.me/${(content?.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{content?.whatsapp}</a></li>
                {content?.whatsapp_group_url && (
                  <li>
                    <a href={content.whatsapp_group_url} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                      WhatsApp Community Group
                    </a>
                  </li>
                )}
                <li><Link to="/admin" data-testid="footer-admin-link" className="hover:text-white transition-colors">Admin Login</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs font-mono tracking-wide">
          <p>© {year} ScholarVerse LAB. All rights reserved.</p>
          <p className="flex items-center gap-2 text-sky/70" data-testid="footer-msme-badge">
            <span className="h-1.5 w-1.5 rotate-45 bg-cherry inline-block" />
            Udyam / MSME Registered · {content?.udyam_id || "UDYAM-UP-66-0048291"}
          </p>
          <p className="text-white/35">Academic credibility · Hands-on learning · Transparency</p>
        </div>
      </div>
    </footer>
  );
}
